import {
  createAsyncThunk,
  createSlice,
  current,
  PayloadAction,
} from '@reduxjs/toolkit';
import { get, post } from '../Services/http';
import Product from '../Types/Product';

export interface QuickOrderState {
  result: QuickOrderProduct[];
  addNewRow: boolean;
  totalPrice: number;
  formattedPrice: string;
  updatePending: boolean;
  errors: Error[];
  focusedArticle?: QuickOrderProduct;
}

interface MappedProduct {
  articleNumber?: string;
  quantity: number;
  note?: string;
  boxAmount: number;
  rollAmount: number;
  total: number;
  uniqueId: string;
  totalAmount: ProductTotal;
}

export interface QuickOrderProduct extends Product {
  totalPrice: number;
  formattedPrice: string;
  note?: string;
  uniqueId: string;
  error: Error;
  totalAmount: ProductTotal;
}

export enum TotalType {
  total,
  boxes,
  rolls,
}

export enum InputId {
  total = 'total',
  boxes = 'boxes',
  rolls = 'rolls',
}

export interface ProductTotal {
  total: number;
  boxesAmount: number;
  rollsAmount: number;
  quantity: number;
}

export interface Error {
  title: string;
  message: string;
  replacement?: string;
}

export interface QuickOrderCsvProduct {
  ArticleNumber: string;
  Quantity: string;
  Note: string;
}

const DEFAULT_STATE: QuickOrderState = {
  result: [],
  addNewRow: false,
  updatePending: false,
  errors: [],
};

export const getProduct = createAsyncThunk(
  'quickorder/getProduct',
  async (articleNumber: string, thunkApi) => {
    try {
      const response = await get(`/api/quickorder/${articleNumber}`);
      return await response.json();
    } catch (error) {
      const data = await error.response.json();
      return thunkApi.rejectWithValue(data);
    }
  }
);

export const getProducts = createAsyncThunk(
  'quickorder/getProducts',
  async (products: QuickOrderCsvProduct[], thunkApi) => {
    if (products) {
      try {
        const response = await post('/api/quickorder/many', products);
        return await response.json();
      } catch (error) {
        const data = await error.response.json();
        return thunkApi.rejectWithValue(data);
      }
    }
  }
);

export const getTotalPrice = createAsyncThunk(
  'quickorder/totalprice',
  async (products: QuickOrderProduct[]) => {
    if (products) {
      const response = await post(
        '/api/quickorder/totalprice',
        mapProducts(products)
      );
      return await response.json();
    }
  }
);

const mapProducts = (products: QuickOrderProduct[]) => {
  const result: MappedProduct[] = [];
  products.map((product) => {
    const mappedproduct: MappedProduct = {
      articleNumber: product.id,
      quantity: product.totalAmount?.total,
      note: product.note ?? '',
      rollAmount: product.totalAmount?.rollsAmount ?? 0,
      boxAmount: product.totalAmount?.boxesAmount ?? 0,
      total: product.totalAmount?.total,
      uniqueId: product.uniqueId,
      totalAmount: product.totalAmount,
    };
    result.push(mappedproduct);
  });

  return result;
};

const QuickOrderSlice = createSlice({
  name: 'quickOrder',
  initialState: DEFAULT_STATE,
  reducers: {
    addResultRow: (
      state,
      action: PayloadAction<{ product: QuickOrderProduct }>
    ) => {
      var articles = state.result;
      articles?.push(action.payload.product);
    },
    removeResultRow: (
      state,
      action: PayloadAction<{ product: QuickOrderProduct }>
    ) => {
      var articles = state.result;
      const index = articles.findIndex(
        (x) => x.uniqueId === action.payload.product.uniqueId
      );

      if (index > -1) {
        articles.splice(index, 1);
      }
    },
    addNoteToRow: (
      state,
      action: PayloadAction<{ product: QuickOrderProduct; note: string }>
    ) => {
      var articles = state.result;
      const index = articles.findIndex(
        (x) => x.uniqueId === action.payload.product.uniqueId
      );
      if (index > -1) {
        state.result[index]!.note = action.payload.note;
      }
    },
    clearResultRow: (state) => {
      state.result = [];
      state.totalPrice = 0;
      state.updatePending = false;
      state.addNewRow = false;
      state.errors = [];
    },
    updateRowQuantity: (
      state,
      action: PayloadAction<{
        product: QuickOrderProduct;
        quantity: number;
        key: any;
      }>
    ) => {
      var articles = state.result;
      var key = action.payload.key;
      const index = articles.findIndex(
        (x) => x.uniqueId === action.payload.product.uniqueId
      );
      if (index > -1) {
        switch (key) {
          case TotalType.total:
            state.result[index]!.totalAmount.total =
              action.payload.quantity;
            break;
          case TotalType.boxes:
            state.result[index]!.totalAmount.boxesAmount =
              action.payload.quantity;
            break;
          case TotalType.rolls:
            state.result[index]!.totalAmount.rollsAmount =
              action.payload.quantity;
        }
      }
    },
    setAddNewRow: (state, action: PayloadAction) => {
      state.addNewRow = true;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getProduct.fulfilled, (state, action) => {
      state.result?.push(action.payload);
      if (action.payload.error) {
        state.errors.push(action.payload.error);
      }
      state.addNewRow = false;
      state.updatePending = false;

      const index = state.result.findIndex(
        (x) => x.uniqueId === action.payload.uniqueId
      );

      if (index > -1) {
        state.focusedArticle == state.result[index];
      }
    });
    builder.addCase(getProduct.pending, (state, action) => {
      state.updatePending = true;
      state.errors = [];
    });
    builder.addCase(getProduct.rejected, (state, action) => {
      // Ignore error since we don't want to show error toast to user
      state.updatePending = false;
    });
    builder.addCase(getTotalPrice.fulfilled, (state, action) => {
      var result = action.payload;
      const { products, errors } = result;

      errors?.forEach((err) => {
        state.errors?.push(err);
      });

      products.forEach((res) => {
        if (res.error) {
          state.errors?.push(res.error);
        }
      });

      state.totalPrice = result.totalPrice;
      state.formattedPrice = result.formattedPrice;
      state.result = products;
      state.updatePending = false;
      state.focusedArticle = products.slice(-1)[0];
    });
    builder.addCase(getTotalPrice.pending, (state, action) => {
      state.updatePending = true;
      state.errors = [];
    });
    builder.addCase(getTotalPrice.rejected, (state, action) => {
      state.updatePending = false;
    });
    builder.addCase(getProducts.fulfilled, (state, action) => {
      var result = action.payload;
      var products = result.products;
      state.totalPrice = result.totalPrice;
      state.formattedPrice = result.formattedPrice;
      state.errors = action.payload.errors;

      products.forEach((res) => {
        state.result?.push(res);
      });
      state.focusedArticle = products.slice(-1)[0];
      state.updatePending = false;
    });
    builder.addCase(getProducts.pending, (state, action) => {
      state.updatePending = true;
      state.errors = [];
    });
    builder.addCase(getProducts.rejected, (state, action) => {
      var error = action.payload;
      state.updatePending = false;
      if (error) {
        state.errors?.push(error);
      }
    });
  },
});

export const QuickOrderActions = QuickOrderSlice.actions;

export default QuickOrderSlice.reducer;
